import TrafficLightManagementApp from './components/traffic-lights/TrafficLightManagementApp.jsx';
import React from 'react'
import {createRoot} from 'react-dom/client'
import AlarmManager from './components/managers/AlarmManager.jsx'
import ApiSettingsForm from './components/api/ApiSettingsForm';
import BulkApiDrawer from './components/drawers/BulkApiDrawer';
import GroupZoneArmingDrawer from './components/drawers/GroupZoneArmingDrawer.jsx';
import ZonesManager from './components/managers/ZonesManager';
import Sensitivity from './components/motion/Sensitivity';
import ImagePromptSelectorManager from "./components/managers/ImagePromptSelectorManager";
import ItemsSummaryDrawer from "./components/drawers/ItemsSummaryDrawer";
import SubAccountSettingsDrawer from "./components/drawers/SubAccountSettingsDrawer";
import DispatcherAccessDrawer from './components/drawers/DispatcherAccessDrawer.jsx';
import GroupAccessDrawer from './components/drawers/GroupAccessDrawer.jsx';
import MaxAlarmsReport from './components/reports/MaxAlarmsReport';
// Import your new React component here
import './components/dashboards/ModelHoursReport'; // Assuming it's a .jsx or .js file
import HornsSettings from './components/HornsSettings.jsx';
import TransportLayerSettings from './components/TransportLayerSettings.jsx';
import LinkSettings from './components/video-source/LinkSettings.jsx';
import DeviceSettings from './components/device/DeviceSettings.jsx';
import DeviceGpioAssignments from './components/device/DeviceGpioAssignments.jsx';
import { AnalyzerNotificationCounter } from './components/AnalyzerComponents';
import AnalyzerEnrichmentPage from './components/analyzers/AnalyzerEnrichmentPage.jsx';
import LiveVideoPlayer from './components/video/LiveVideoPlayer.jsx';
import DisplayWallStreamsEditor from './components/display-walls/DisplayWallStreamsEditor.jsx';
import DisplayWallViewer from './components/display-walls/DisplayWallViewer.jsx';
import StatusNotificationCenter from './components/items/StatusNotificationCenter.jsx';
import CloneManyModal from './components/item/CloneManyModal.jsx';
import StreamManagementApp from './components/stream-management/StreamManagementApp.jsx';
import AlarmCommentsPanel from './components/alarms/AlarmCommentsPanel.jsx';
import HornAudioPanel from './components/alarms/HornAudioPanel.jsx';
import CleanImageDrawer from './components/item/CleanImageDrawer.jsx';
import AlarmCleanupDrawer from './components/alarms/AlarmCleanupDrawer.jsx';
import MarkersManager from './components/managers/MarkersManager.jsx';
import GroupEnrichmentModal from './components/groups/GroupEnrichmentModal.jsx';
import GroupMapDrawer from './components/groups/GroupMapDrawer.jsx';
import GpsPointPickerField from './components/items/GpsPointPickerField.jsx';
import ItemEnrichmentModal from './components/items/ItemEnrichmentModal.jsx';
import AnalyzerRowDrawer from './components/analyzers/AnalyzerRowDrawer.jsx';
import FluxPauseModal from './components/items/FluxPauseModal.jsx';
import ItemCustomModelManager from './components/models/ItemCustomModelManager.jsx';
import LabelsComparePage from './components/labels/LabelsComparePage.jsx';
import { OcrDocumentsPage, OcrDocumentDetailsPage } from './components/ocr-documents/OcrDocumentsPage.jsx';
import UserFilesPage from './components/user-files/UserFilesPage.jsx';
import GateManagementApp from './components/gpio/GateManagementApp.jsx';
import GpioAccessListManagementApp from './components/gpio/GpioAccessListManagementApp.jsx';
import PersonPlateAssignments from './components/gpio/PersonPlateAssignments.jsx';
import GateTriggerControls from './components/gpio/GateTriggerControls.jsx';

const truthyDebugValues = new Set(['1', 'true', 'yes', 'y', 'on', 'enable', 'enabled', 'debug', 't']);
const falsyDebugValues = new Set(['0', 'false', 'no', 'n', 'off', 'disable', 'disabled', 'f']);

const parseBooleanFlag = (value) => {
    if (typeof value === 'boolean') {
        return value;
    }

    if (typeof value === 'number') {
        if (Number.isNaN(value)) {
            return null;
        }
        return value !== 0;
    }

    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (truthyDebugValues.has(normalized)) {
            return true;
        }
        if (falsyDebugValues.has(normalized)) {
            return false;
        }
    }

    return null;
};

const resolveLivePlayerDebugOverride = () => {
    if (typeof window === 'undefined') {
        return null;
    }

    if (typeof window.__LIVE_PLAYER_DEBUG_OVERRIDE !== 'undefined') {
        return window.__LIVE_PLAYER_DEBUG_OVERRIDE;
    }

    try {
        const params = new URLSearchParams(window.location.search);
        const rawParam = params.get('live_player_debug');
        if (rawParam !== null) {
            const normalized = rawParam.trim().toLowerCase();
            if (['reset', 'clear', 'auto', 'default'].includes(normalized)) {
                try {
                    window.localStorage.removeItem('livePlayerDebug');
                } catch (storageError) {
                    console.warn('Failed to clear livePlayerDebug from storage', storageError);
                }
                window.__LIVE_PLAYER_DEBUG_OVERRIDE = null;
                return null;
            }

            const parsedParam = parseBooleanFlag(rawParam);
            if (parsedParam !== null) {
                window.__LIVE_PLAYER_DEBUG_OVERRIDE = parsedParam;
                try {
                    window.localStorage.setItem('livePlayerDebug', parsedParam ? 'true' : 'false');
                } catch (storageError) {
                    console.warn('Failed to persist livePlayerDebug override', storageError);
                }
                return parsedParam;
            }
        }
    } catch (paramError) {
        console.warn('Failed to read live_player_debug query parameter', paramError);
    }

    try {
        const storedValue = window.localStorage.getItem('livePlayerDebug');
        const parsedStoredValue = parseBooleanFlag(storedValue);
        if (parsedStoredValue !== null) {
            window.__LIVE_PLAYER_DEBUG_OVERRIDE = parsedStoredValue;
            return parsedStoredValue;
        }
    } catch (storageError) {
        console.warn('Failed to read livePlayerDebug override from storage', storageError);
    }

    if (typeof window.LiveVideoPlayerDebug !== 'undefined') {
        const parsedGlobalValue = parseBooleanFlag(window.LiveVideoPlayerDebug);
        if (parsedGlobalValue !== null) {
            window.__LIVE_PLAYER_DEBUG_OVERRIDE = parsedGlobalValue;
            return parsedGlobalValue;
        }
    }

    window.__LIVE_PLAYER_DEBUG_OVERRIDE = null;
    return null;
};

const livePlayerDebugOverride = resolveLivePlayerDebugOverride();

// get model_analyzers from react-root div data-analyzers-json attribute and pass to render
let model_analyzers = [];
let disabledAlarmManagement = false;
const alarmManagementRoot = document.getElementById('react-root');
if (alarmManagementRoot) {

    try {
        model_analyzers = JSON.parse(alarmManagementRoot.getAttribute('data-analyzers-json'))
    } catch (e) {
        console.error('Error parsing model analyzers', e);
    }
// convert model_analyzers to array
    model_analyzers = Object.keys(model_analyzers).map(key => model_analyzers[key]);
    let availableModels = [];
    try {
        availableModels = JSON.parse(alarmManagementRoot.dataset.availableModels || '{}');
        // Convert object to array if necessary, preserving original structure if already array
        if (typeof availableModels === 'object' && !Array.isArray(availableModels) && availableModels !== null) {
            availableModels = Object.values(availableModels); // Assuming values are the model objects
        } else if (!Array.isArray(availableModels)) {
            availableModels = [];
        }
    } catch (e) { console.error("Error parsing availableModels:", e); availableModels = []; }
    const rootm = createRoot(alarmManagementRoot)
// get alarms from the input hidden field input.alarms in the blade file from json and pass to render
    let alarms = [];
    try {
        alarms = JSON.parse(document.querySelector('input.alarmsData').value) || [];
    } catch (e) {
        console.error('Error parsing alarms', e);
    }
    console.log('alarms', alarms);
    // get disabled from data-disabled attribute
    disabledAlarmManagement = alarmManagementRoot.dataset.disabled === 'true';
    const selectedHornId = Number.parseInt(alarmManagementRoot.dataset.selectedHornId || '-1', 10);
    rootm.render(
        <AlarmManager
            initialAllTags={JSON.parse(alarmManagementRoot.dataset.initialTags || 'null')}
            alarms={alarms}
            model_analyzers={model_analyzers}
            disabled={disabledAlarmManagement}
            availableModels={availableModels}
            initialHornId={Number.isFinite(selectedHornId) ? selectedHornId : -1}
        />
    )
}

// Initialize Max Alarms Report
const maxAlarmsReportContainer = document.getElementById('max-alarms-report-container');
if (maxAlarmsReportContainer) {
    const rootMaxAlarms = createRoot(maxAlarmsReportContainer);
    const token = maxAlarmsReportContainer.getAttribute('data-token');
    const apiUrl = maxAlarmsReportContainer.getAttribute('data-api-url');

    // The clientsData variable is defined in the blade view
    if (typeof clientsData !== 'undefined') {
        rootMaxAlarms.render(
            <MaxAlarmsReport
                accessToken={token}
                clients={clientsData}
                apiUrl={apiUrl}
            />
        );
    } else {
        console.error('clientsData is not defined');
    }
}

const apiSettingsRoot = document.getElementById('api-settings-root');
if (apiSettingsRoot) {
    const roota = createRoot(apiSettingsRoot);
    let showSms = (apiSettingsRoot.getAttribute('data-show-sms')=="1");
    roota.render(
        <ApiSettingsForm
            item={JSON.parse(apiSettingsRoot.dataset.item)}
            errors={JSON.parse(apiSettingsRoot.dataset.errors)}
            disabled={apiSettingsRoot.dataset.disabled === 'true'}
            showSms={showSms}
        />
    );
}


const bulkUpdateApiSettingsRoot = document.getElementById('bulk-update-api-settings-root');
if (bulkUpdateApiSettingsRoot) {
    const rootb = createRoot(bulkUpdateApiSettingsRoot);
    let showSms = (bulkUpdateApiSettingsRoot.getAttribute('data-show-sms')=="1");
    rootb.render(
        <BulkApiDrawer showSms={showSms}/>
    );
}

const groupZoneArmingRoot = document.getElementById('group-zone-arming-root');
if (groupZoneArmingRoot) {
    const rootGroupZoneArming = createRoot(groupZoneArmingRoot);

    rootGroupZoneArming.render(
        <GroupZoneArmingDrawer
            metaUrl={groupZoneArmingRoot.dataset.metaUrl ?? ''}
            updateUrl={groupZoneArmingRoot.dataset.updateUrl ?? ''}
        />
    );
}

const zoneEditorRoot = document.getElementById('react-zones');
if (zoneEditorRoot) {
    const rootz = createRoot(zoneEditorRoot);
    rootz.render(<ZonesManager/>);
}

const itemModelCustomRoot = document.getElementById('item-model-custom-root');
if (itemModelCustomRoot) {
    const rootItemModelCustom = createRoot(itemModelCustomRoot);
    rootItemModelCustom.render(<ItemCustomModelManager />);
}

const markersManagerRoot = document.getElementById('markers-manager-root');
if (markersManagerRoot) {
    const rootMarkers = createRoot(markersManagerRoot);
    const bootstrapPayload = {
        baseUrl: markersManagerRoot.dataset.baseUrl || '',
        baseurl: markersManagerRoot.dataset.baseurl || '',
        maxSets: markersManagerRoot.dataset.maxSets || '0',
        disabled: markersManagerRoot.dataset.disabled || 'false',
        colorSchemes: markersManagerRoot.dataset.colorSchemes || '{}',
        templates: markersManagerRoot.dataset.templates || '{}',
    };
    rootMarkers.render(<MarkersManager bootstrap={bootstrapPayload} />);
}

const sensitivityRoot = document.getElementById('sensitivity-drawer-root');
if (sensitivityRoot) {
    const rootSens = createRoot(sensitivityRoot);

    const initialThreshold = parseInt(sensitivityRoot.dataset.initialThreshold, 10);
    const initialSensitivity = parseInt(sensitivityRoot.dataset.initialSensitivity, 10);
    const saveUrl = sensitivityRoot.dataset.saveUrl;
    const canEdit = sensitivityRoot.dataset.canEdit;


    rootSens.render(<Sensitivity
        initialThreshold={initialThreshold}
        initialSensitivity={initialSensitivity}
        saveUrl={saveUrl}
        canEdit={canEdit}
    />);
}

const containerLabeledFilesRoot = document.getElementById('labeled-files-root');
if (containerLabeledFilesRoot) {
    createRoot(containerLabeledFilesRoot).render(<ImagePromptSelectorManager />);
}

const containerSummaryDrawerRoot = document.getElementById('summary-item-root');
if (containerSummaryDrawerRoot) {
    const rootSummary = createRoot(containerSummaryDrawerRoot);
    const allTags = JSON.parse(containerSummaryDrawerRoot.dataset.allTags)??[];
    const itemApi = containerSummaryDrawerRoot.dataset.itemApi??"";
    rootSummary.render(<ItemsSummaryDrawer
        allTags={allTags}
        itemApi={itemApi}
        /> );

}

const subAccountSettingsRoot = document.getElementById('sub-account-settings-root');
if (subAccountSettingsRoot) {
    const rootSubAccountSettings = createRoot(subAccountSettingsRoot);
    const getSubAccountApi = subAccountSettingsRoot.dataset.getSubAccountApi ?? "";
    const updateSubAccountApi = subAccountSettingsRoot.dataset.updateSubAccountApi ?? "";
    rootSubAccountSettings.render(<SubAccountSettingsDrawer
        getSubAccountApi={getSubAccountApi}
        updateSubAccountApi={updateSubAccountApi}
        />);
}

const dispatcherSettingsRoot = document.getElementById('dispatcher-settings-root');
if (dispatcherSettingsRoot) {
    const rootDispatcherSettings = createRoot(dispatcherSettingsRoot);
    const getDispatcherApi = dispatcherSettingsRoot.dataset.getDispatcherApi ?? '';
    const updateDispatcherApi = dispatcherSettingsRoot.dataset.updateDispatcherApi ?? '';
    rootDispatcherSettings.render(
        <DispatcherAccessDrawer
            getDispatcherApi={getDispatcherApi}
            updateDispatcherApi={updateDispatcherApi}
        />
    );
}

const groupAccessRoot = document.getElementById('group-access-drawer-root');
if (groupAccessRoot) {
    const rootGroupAccess = createRoot(groupAccessRoot);
    const infoUrl = groupAccessRoot.dataset.infoUrl ?? '';
    const updateUrl = groupAccessRoot.dataset.updateUrl ?? '';

    rootGroupAccess.render(
        <GroupAccessDrawer
            infoUrl={infoUrl}
            updateUrl={updateUrl}
        />
    );
}

// --- Enable #submit-btn only when both components are loaded ---
(function() {
    let apiSettingsLoaded = !apiSettingsRoot;
    let zoneEditorLoaded = !zoneEditorRoot;

    function tryEnableSubmitBtn() {
        if (apiSettingsLoaded && zoneEditorLoaded) {
            if (typeof $ !== 'undefined') {
                $('#submit-btn').attr('disabled', false);
            } else {
                console.error('jQuery is not loaded, cannot enable submit button.');
            }
        }
    }

    window.addEventListener('apiSettingsForm:loaded', function() {
        apiSettingsLoaded = true;
        tryEnableSubmitBtn();
    });
    window.addEventListener('zoneEditorManager:loaded', function() {
        zoneEditorLoaded = true;
        tryEnableSubmitBtn();
    });
})();


const transportLayerRoot = document.getElementById('transport-layer-settings-root');
if (transportLayerRoot) {
    const roott = createRoot(transportLayerRoot);
    let items = document.getElementById('transport_layer_config')?.value || '[]';
    try {
        JSON.parse(items);
    } catch (e) {
        items = '[]';
    }
    // Ensure items is always an array
    if (!items.trim().startsWith('[')) {
        items = '[]';
    }
    roott.render(
        <TransportLayerSettings
            items={items}
        />
    );
}

const hornsRoot = document.getElementById('horns-settings-root');
if (hornsRoot) {
    const rootHorns = createRoot(hornsRoot);
    let items = document.getElementById('horns_config')?.value || '[]';
    try {
        JSON.parse(items);
    } catch (e) {
        items = '[]';
    }
    if (!items.trim().startsWith('[')) {
        items = '[]';
    }
    const clientTwoWayAudio = parseBooleanFlag(hornsRoot.dataset?.clientTwoWayAudio) === true;
    rootHorns.render(
        <HornsSettings
            items={items}
            clientTwoWayAudio={clientTwoWayAudio}
        />
    );
}

const deviceGpioAssignmentsRoot = document.getElementById('device-gpio-assignments-root');
if (deviceGpioAssignmentsRoot) {
    const rootDeviceGpioAssignments = createRoot(deviceGpioAssignmentsRoot);

    let availableGates = [];
    let initialRelayMeta = [];
    let initialAssignments = [];
    try {
        availableGates = JSON.parse(deviceGpioAssignmentsRoot.dataset.availableGates || '[]');
    } catch (error) {
        console.error('Failed to parse available GPIO gates', error);
    }
    try {
        initialRelayMeta = JSON.parse(deviceGpioAssignmentsRoot.dataset.initialRelayMeta || '[]');
    } catch (error) {
        console.error('Failed to parse initial GPIO relay meta', error);
    }
    try {
        initialAssignments = JSON.parse(deviceGpioAssignmentsRoot.dataset.initialAssignments || '[]');
    } catch (error) {
        console.error('Failed to parse initial GPIO assignments', error);
    }

    rootDeviceGpioAssignments.render(
        <DeviceGpioAssignments
            availableTrafficLights={JSON.parse(deviceGpioAssignmentsRoot.dataset.availableTrafficLights || '[]')}
            initialTrafficLightAssignments={JSON.parse(deviceGpioAssignmentsRoot.dataset.initialTrafficLightAssignments || '[]')}
            validationErrors={JSON.parse(deviceGpioAssignmentsRoot.dataset.validationErrors || '{}')}
            availableGates={availableGates}
            initialRelayMeta={initialRelayMeta}
            initialAssignments={initialAssignments}
            triggerUrlTemplate={deviceGpioAssignmentsRoot.dataset.triggerUrlTemplate || ''}
            testDeviceId={deviceGpioAssignmentsRoot.dataset.testDeviceId || ''}
            auditContext={deviceGpioAssignmentsRoot.dataset.auditContext ? JSON.parse(deviceGpioAssignmentsRoot.dataset.auditContext) : null}
            disabled={deviceGpioAssignmentsRoot.dataset.disabled === 'true'}
        />
    );
}

const linkSettingsRoot = document.getElementById('link-settings-root');
if (linkSettingsRoot) {
    const rootLink = createRoot(linkSettingsRoot);
    const parsedInitialHornId = Number.parseInt(linkSettingsRoot.dataset.initialHornId || '-1', 10);
    rootLink.render(
        <LinkSettings
            initialLink={linkSettingsRoot.dataset.initialLink || ''}
            initialUploadedLink={linkSettingsRoot.dataset.initialUploadedLink || '0'}
            initialFullframeProcessing={linkSettingsRoot.dataset.initialFullframeProcessing === 'true'}
            initialNvrVendor={linkSettingsRoot.dataset.initialNvrVendor || ''}
            availableHorns={JSON.parse(linkSettingsRoot.dataset.availableHorns || '[]')}
            initialHornId={Number.isFinite(parsedInitialHornId) ? parsedInitialHornId : -1}
            disabled={linkSettingsRoot.dataset.disabled === 'true'}
            item={JSON.parse(linkSettingsRoot.dataset.item || '{}')}
            canUpload={linkSettingsRoot.dataset.canUpload === 'true'}
            uploadUrl={linkSettingsRoot.dataset.uploadUrl || ''}
            uploadFilesUrl={linkSettingsRoot.dataset.uploadFilesUrl || ''}
            maxUploadSize={linkSettingsRoot.dataset.maxUploadSize || '0'}
            nvrVendors={JSON.parse(linkSettingsRoot.dataset.nvrVendors || '[]')}
            transportDevices={JSON.parse(linkSettingsRoot.dataset.transportDevices || '[]')}
            isOwner={linkSettingsRoot.dataset.isOwner === 'true'}
            isAdmin={linkSettingsRoot.dataset.isAdmin === 'true'}
            errors={JSON.parse(linkSettingsRoot.dataset.errors || '{}')}
        />
    );
}

const cleanImageRoot = document.getElementById('clean-image-react-root');
if (cleanImageRoot) {
    const rootCleanImage = createRoot(cleanImageRoot);
    let initialMeta = null;
    try {
        initialMeta = cleanImageRoot.dataset.initialMeta ? JSON.parse(cleanImageRoot.dataset.initialMeta) : null;
    } catch (error) {
        console.warn('Unable to parse clean image metadata', error);
        initialMeta = null;
    }

    rootCleanImage.render(
        <CleanImageDrawer
            statusUrl={cleanImageRoot.dataset.statusUrl || ''}
            captureUrl={cleanImageRoot.dataset.captureUrl || ''}
            deleteUrl={cleanImageRoot.dataset.deleteUrl || ''}
            initialMeta={initialMeta}
            itemName={cleanImageRoot.dataset.itemName || ''}
            itemId={cleanImageRoot.dataset.itemId || ''}
            canCapture={cleanImageRoot.dataset.canCapture === 'true'}
        />
    );
}

const alarmCleanupRoot = document.getElementById('alarm-cleanup-drawer-root');
if (alarmCleanupRoot) {
    const rootCleanup = createRoot(alarmCleanupRoot);
    let cleanupItems = [];
    let cleanupGroups = [];
    try {
        cleanupItems = JSON.parse(alarmCleanupRoot.dataset.items || '[]');
    } catch (error) {
        console.warn('Failed to parse cleanup drawer items payload', error);
    }
    try {
        cleanupGroups = JSON.parse(alarmCleanupRoot.dataset.groups || '[]');
    } catch (error) {
        console.warn('Failed to parse cleanup drawer groups payload', error);
    }

    rootCleanup.render(
        <AlarmCleanupDrawer
            items={cleanupItems}
            groups={cleanupGroups}
            storeUrl={alarmCleanupRoot.dataset.storeUrl || ''}
            indexUrl={alarmCleanupRoot.dataset.indexUrl || ''}
            ownerName={alarmCleanupRoot.dataset.ownerName || ''}
        />
    );
}

const deviceSettingsRoot = document.getElementById('device-settings-root');
if (deviceSettingsRoot) {
    const rootDeviceSettings = createRoot(deviceSettingsRoot);

    // Parse data from dataset
    const initialAccessUsers = JSON.parse(deviceSettingsRoot.dataset.initialAccessUsers || '[]');
    const deviceId = deviceSettingsRoot.dataset.deviceId;
    const isOwner = deviceSettingsRoot.dataset.isOwner === 'true';
    const isAdmin = deviceSettingsRoot.dataset.isAdmin === 'true';
    const currentUserId = deviceSettingsRoot.dataset.currentUserId;
    const initialSystemValue = deviceSettingsRoot.dataset.initialSystemValue === 'true';
    const initialRestrictedAccess = deviceSettingsRoot.dataset.initialRestrictedAccess === 'true';
    const disabled = deviceSettingsRoot.dataset.disabled === 'true';
    const initialOwnerId = deviceSettingsRoot.dataset.initialOwnerId;
    const ownerOptions = JSON.parse(deviceSettingsRoot.dataset.ownerOptions || '[]');

    // API URLs from Laravel routes
    const availableUsersUrl = deviceSettingsRoot.dataset.availableUsersUrl;
    const subAccountsUrl = deviceSettingsRoot.dataset.subAccountsUrl;
    const accessUsersUrl = deviceSettingsRoot.dataset.accessUsersUrl;
    const ownershipImpactUrl = deviceSettingsRoot.dataset.ownershipImpactUrl;
    const updateAccessUrl = deviceSettingsRoot.dataset.updateAccessUrl;
    const authToken = deviceSettingsRoot.dataset.authToken;

    rootDeviceSettings.render(
        <DeviceSettings
            initialSystemValue={initialSystemValue}
            deviceId={deviceId}
            isOwner={isOwner}
            isAdmin={isAdmin}
            currentUserId={currentUserId}
            initialAccessUsers={initialAccessUsers}
            initialRestrictedAccess={initialRestrictedAccess}
            initialOwnerId={initialOwnerId}
            ownerOptions={ownerOptions}
            disabled={disabled}
            availableUsersUrl={availableUsersUrl}
            subAccountsUrl={subAccountsUrl}
            accessUsersUrl={accessUsersUrl}
            ownershipImpactUrl={ownershipImpactUrl}
            updateAccessUrl={updateAccessUrl}
            authToken={authToken}
        />
    );
}

// Initialize Analyzer Notification Counter
const analyzerNotificationCounterRoot = document.getElementById('analyzer-notification-counter');
if (analyzerNotificationCounterRoot) {
    const rootAnalyzerCounter = createRoot(analyzerNotificationCounterRoot);
    const userId = analyzerNotificationCounterRoot.getAttribute('data-user-id');

    rootAnalyzerCounter.render(
        <AnalyzerNotificationCounter userId={userId} />
    );
}

const analyzerEnrichmentRoot = document.getElementById('analyzer-enrichment-root');
if (analyzerEnrichmentRoot) {
    const rootAnalyzerEnrichment = createRoot(analyzerEnrichmentRoot);
    let models = [];
    let groups = [];
    let clients = [];
    try {
        models = JSON.parse(analyzerEnrichmentRoot.dataset.models || '[]');
    } catch (error) {
        console.error('Failed to parse analyzer enrichment models payload', error);
    }
    try {
        groups = JSON.parse(analyzerEnrichmentRoot.dataset.groups || '[]');
    } catch (error) {
        console.error('Failed to parse analyzer enrichment groups payload', error);
    }
    try {
        clients = JSON.parse(analyzerEnrichmentRoot.dataset.clients || '[]');
    } catch (error) {
        console.error('Failed to parse analyzer enrichment clients payload', error);
    }

    rootAnalyzerEnrichment.render(
        <AnalyzerEnrichmentPage
            clients={clients}
            models={models}
            groups={groups}
            listUrl={analyzerEnrichmentRoot.dataset.listUrl || ''}
            groupsUrl={analyzerEnrichmentRoot.dataset.groupsUrl || ''}
            canSelectAllClients={analyzerEnrichmentRoot.dataset.canSelectAll === 'true'}
            upsertUrl={analyzerEnrichmentRoot.dataset.upsertUrl || ''}
            deleteUrlTemplate={analyzerEnrichmentRoot.dataset.deleteUrlTemplate || ''}
        />
    );
}

const groupEnrichmentRoot = document.getElementById('group-enrichment-modal-root');
if (groupEnrichmentRoot) {
    const rootGroupEnrichment = createRoot(groupEnrichmentRoot);
    let models = [];
    try {
        models = JSON.parse(groupEnrichmentRoot.dataset.models || '[]');
    } catch (error) {
        console.error('Failed to parse group enrichment models payload', error);
    }

    if (models.length) {
        rootGroupEnrichment.render(
            <GroupEnrichmentModal
                contextsUrlTemplate={groupEnrichmentRoot.dataset.contextsUrlTemplate || ''}
                updateUrlTemplate={groupEnrichmentRoot.dataset.updateUrlTemplate || ''}
                models={models}
            />
        );
    }
}

const groupMapManagerRoot = document.getElementById('group-map-manager-root');
if (groupMapManagerRoot) {
    const rootGroupMapManager = createRoot(groupMapManagerRoot);
    rootGroupMapManager.render(
        <GroupMapDrawer
            indexUrl={groupMapManagerRoot.dataset.indexUrl || ''}
            storeUrl={groupMapManagerRoot.dataset.storeUrl || ''}
            uploadUrl={groupMapManagerRoot.dataset.uploadUrl || ''}
            settingsUrl={groupMapManagerRoot.dataset.settingsUrl || ''}
            updateUrlTemplate={groupMapManagerRoot.dataset.updateUrlTemplate || ''}
            deleteUrlTemplate={groupMapManagerRoot.dataset.deleteUrlTemplate || ''}
            canManage={groupMapManagerRoot.dataset.canManage === 'true'}
            mode={groupMapManagerRoot.dataset.mode || 'manage'}
            openFunctionName={groupMapManagerRoot.dataset.openFunctionName || ''}
        />
    );
}

const itemGroupMapRoot = document.getElementById('item-group-map-root');
if (itemGroupMapRoot) {
    const rootItemGroupMap = createRoot(itemGroupMapRoot);
    rootItemGroupMap.render(
        <GroupMapDrawer
            indexUrl={itemGroupMapRoot.dataset.indexUrl || ''}
            storeUrl={itemGroupMapRoot.dataset.storeUrl || ''}
            uploadUrl={itemGroupMapRoot.dataset.uploadUrl || ''}
            settingsUrl={itemGroupMapRoot.dataset.settingsUrl || ''}
            updateUrlTemplate={itemGroupMapRoot.dataset.updateUrlTemplate || ''}
            deleteUrlTemplate={itemGroupMapRoot.dataset.deleteUrlTemplate || ''}
            defaultGroupName={itemGroupMapRoot.dataset.groupName || ''}
            canManage={itemGroupMapRoot.dataset.canManage === 'true'}
            mode={itemGroupMapRoot.dataset.mode || 'item'}
            currentItemId={itemGroupMapRoot.dataset.currentItemId || ''}
            openFunctionName={itemGroupMapRoot.dataset.openFunctionName || ''}
        />
    );
}

const alertGroupMapRoot = document.getElementById('alert-group-map-root') || document.getElementById('alerts-group-map-root');
if (alertGroupMapRoot) {
    const rootAlertGroupMap = createRoot(alertGroupMapRoot);
    rootAlertGroupMap.render(
        <GroupMapDrawer
            indexUrl={alertGroupMapRoot.dataset.indexUrl || ''}
            storeUrl={alertGroupMapRoot.dataset.storeUrl || ''}
            uploadUrl={alertGroupMapRoot.dataset.uploadUrl || ''}
            settingsUrl={alertGroupMapRoot.dataset.settingsUrl || ''}
            updateUrlTemplate={alertGroupMapRoot.dataset.updateUrlTemplate || ''}
            deleteUrlTemplate={alertGroupMapRoot.dataset.deleteUrlTemplate || ''}
            defaultGroupName={alertGroupMapRoot.dataset.groupName || ''}
            canManage={alertGroupMapRoot.dataset.canManage === 'true'}
            mode={alertGroupMapRoot.dataset.mode || 'alarm'}
            currentItemId={alertGroupMapRoot.dataset.currentItemId || ''}
            alarmWindowStart={alertGroupMapRoot.dataset.alarmWindowStart || ''}
            alarmWindowEnd={alertGroupMapRoot.dataset.alarmWindowEnd || ''}
            openFunctionName={alertGroupMapRoot.dataset.openFunctionName || ''}
        />
    );
}

const itemEnrichmentRoot = document.getElementById('item-enrichment-modal-root');
if (itemEnrichmentRoot) {
    const rootItemEnrichment = createRoot(itemEnrichmentRoot);
    let models = [];
    try {
        models = JSON.parse(itemEnrichmentRoot.dataset.models || '[]');
    } catch (error) {
        console.error('Failed to parse item enrichment models payload', error);
    }

    if (models.length) {
        rootItemEnrichment.render(
            <ItemEnrichmentModal
                itemId={itemEnrichmentRoot.dataset.itemId || ''}
                currentStatus={itemEnrichmentRoot.dataset.currentStatus || ''}
                contextsUrlTemplate={itemEnrichmentRoot.dataset.contextsUrlTemplate || ''}
                updateUrlTemplate={itemEnrichmentRoot.dataset.updateUrlTemplate || ''}
                models={models}
            />
        );
    }
}

const gpsPickerRoots = document.querySelectorAll('[data-gps-picker-props]');
if (gpsPickerRoots.length) {
    gpsPickerRoots.forEach((node) => {
        if (node.dataset.gpsPickerMounted === 'true') {
            return;
        }

        let props = {};
        try {
            props = JSON.parse(node.dataset.gpsPickerProps || '{}');
        } catch (error) {
            console.error('Failed to parse GPS picker props', error);
        }

        const root = createRoot(node);
        node.dataset.gpsPickerMounted = 'true';
        root.render(<GpsPointPickerField {...props} />);
    });
}

const alarmCommentsRootNode = document.getElementById('alarm-comments-root');
if (alarmCommentsRootNode) {
    const dataset = alarmCommentsRootNode.dataset ?? {};
    const alarmId = dataset.alarmId ?? null;
    const fetchUrl = dataset.fetchUrl ?? '';
    const storeUrl = dataset.storeUrl ?? '';
    if (alarmId && fetchUrl) {
        const root = createRoot(alarmCommentsRootNode);
        const canCommentFlag = parseBooleanFlag(dataset.canComment);
        const currentUserName = dataset.currentUserName ?? '';
        const currentUserEmail = dataset.currentUserEmail ?? '';
        const initialCountRaw = Number.parseInt(dataset.initialCount ?? '0', 10);
        const initialCount = Number.isNaN(initialCountRaw) ? 0 : initialCountRaw;
        const currentUser = (currentUserName || currentUserEmail)
            ? { name: currentUserName, email: currentUserEmail }
            : null;

        root.render(
            <AlarmCommentsPanel
                alarmId={alarmId}
                fetchUrl={fetchUrl}
                storeUrl={storeUrl}
                canComment={canCommentFlag !== null ? canCommentFlag : true}
                currentUser={currentUser}
                initialCount={initialCount}
            />
        );
    }
}

const hornAudioRootNode = document.getElementById('horn-audio-root');
if (hornAudioRootNode) {
    let hornProps = {};
    try {
        hornProps = JSON.parse(hornAudioRootNode.dataset.hornAudioProps || '{}');
    } catch (error) {
        console.error('Failed to parse horn audio props', error);
    }
    const hornUserId = hornAudioRootNode.dataset.userId || '';
    if (hornProps.alarm_id && hornProps.horn_id && hornUserId) {
        const hornRoot = createRoot(hornAudioRootNode);
        hornRoot.render(
            <HornAudioPanel
                alarmId={hornProps.alarm_id}
                hornId={hornProps.horn_id}
                twoWay={hornProps.two_way === true}
                startUrl={hornProps.start_url}
                intentUrl={hornProps.intent_url}
                stopUrl={hornProps.stop_url}
                grantStreamsUrl={hornProps.grant_streams_url}
                streamStatusUrl={hornProps.stream_status_url}
                userId={hornUserId}
            />
        );
    }
}

const ocrDocumentsRootNode = document.getElementById('ocr-documents-root');
if (ocrDocumentsRootNode) {
    let props = {};
    try {
        props = JSON.parse(ocrDocumentsRootNode.dataset.ocrDocumentsProps || '{}');
    } catch (error) {
        console.error('Failed to parse OCR documents props', error);
    }

    const root = createRoot(ocrDocumentsRootNode);
    root.render(<OcrDocumentsPage {...props} />);
}

const ocrDocumentDetailsRootNode = document.getElementById('ocr-document-details-root');
if (ocrDocumentDetailsRootNode) {
    let props = {};
    try {
        props = JSON.parse(ocrDocumentDetailsRootNode.dataset.ocrDocumentDetailsProps || '{}');
    } catch (error) {
        console.error('Failed to parse OCR document details props', error);
    }

    const root = createRoot(ocrDocumentDetailsRootNode);
    root.render(<OcrDocumentDetailsPage {...props} />);
}

const userFilesRootNode = document.getElementById('user-files-root');
if (userFilesRootNode) {
    let props = {};
    try {
        props = JSON.parse(userFilesRootNode.dataset.userFilesProps || '{}');
    } catch (error) {
        console.error('Failed to parse user files props', error);
    }

    const root = createRoot(userFilesRootNode);
    root.render(<UserFilesPage {...props} />);
}

const analyzerRowDrawerRoot = document.getElementById('analyzer-row-drawer-root');
if (analyzerRowDrawerRoot) {
    const rootRowDrawer = createRoot(analyzerRowDrawerRoot);
    rootRowDrawer.render(<AnalyzerRowDrawer />);
}

const notificationCenterRoot = document.getElementById('item-notification-center-root');
if (notificationCenterRoot) {
    const root = createRoot(notificationCenterRoot);
    const {
        itemId,
        initialTitle,
        initialMessage,
        emptyLabel,
        updatedPrefix,
        statusFallback,
        autoSubscribe
    } = notificationCenterRoot.dataset;

    root.render(
        <StatusNotificationCenter
            itemId={itemId}
            initialTitle={initialTitle}
            initialMessage={initialMessage}
            emptyLabel={emptyLabel}
            updatedPrefix={updatedPrefix}
            statusFallback={statusFallback}
            autoSubscribe={autoSubscribe === 'true'}
        />
    );
}

const mountLiveVideoPlayers = (scope = document) => {
    const livePlayerNodes = scope.querySelectorAll('[data-live-player-props]');
    if (!livePlayerNodes.length) {
        return;
    }

    livePlayerNodes.forEach((node) => {
        if (node.dataset.livePlayerMounted === 'true') {
            return;
        }

        let props = {};
        try {
            props = JSON.parse(node.dataset.livePlayerProps || '{}');
        } catch (error) {
            console.error('Failed to parse live player props', error);
        }

        if (livePlayerDebugOverride !== null) {
            props.debug = livePlayerDebugOverride;
        }

        const root = createRoot(node);
        node.dataset.livePlayerMounted = 'true';
        root.render(<LiveVideoPlayer {...props} />);
    });
};

window.mountLiveVideoPlayers = mountLiveVideoPlayers;
mountLiveVideoPlayers();

const cloneManyRoot = document.getElementById('clone-many-root');
if (cloneManyRoot) {
    const rootCloneMany = createRoot(cloneManyRoot);
    let devices = [];
    try {
        devices = JSON.parse(cloneManyRoot.dataset.devices || '[]');
    } catch (error) {
        console.error('Failed to parse clone-many devices payload', error);
    }

    const urlTemplate = cloneManyRoot.dataset.urlTemplate || '';
    rootCloneMany.render(<CloneManyModal urlTemplate={urlTemplate} devices={devices} />);
}

const fluxPauseRoot = document.getElementById('flux-pause-root');
if (fluxPauseRoot) {
    const rootFluxPause = createRoot(fluxPauseRoot);
    rootFluxPause.render(
        <FluxPauseModal
            pauseUrlTemplate={fluxPauseRoot.dataset.pauseUrlTemplate || ''}
            resumeUrlTemplate={fluxPauseRoot.dataset.resumeUrlTemplate || ''}
        />
    );
}

const labelsCompareRoot = document.getElementById('labels-compare-root');
if (labelsCompareRoot) {
    const rootLabelsCompare = createRoot(labelsCompareRoot);
    let props = {};
    try {
        props = JSON.parse(labelsCompareRoot.dataset.payload || '{}');
    } catch (error) {
        console.error('Failed to parse labels compare payload', error);
    }

    rootLabelsCompare.render(<LabelsComparePage payload={props} />);
}

const displayWallStreamsNodes = document.querySelectorAll('[data-display-wall-streams-props]');
if (displayWallStreamsNodes.length) {
    displayWallStreamsNodes.forEach((node) => {
        if (node.dataset.displayWallStreamsMounted === 'true') {
            return;
        }

        const rawPayload = node.dataset.displayWallStreamsProps || '{}';
        const isBase64 = node.dataset.displayWallStreamsPropsB64 === '1';

        let jsonPayload = rawPayload;
        if (isBase64) {
            try {
                jsonPayload = atob(rawPayload);
            } catch (decodeError) {
                console.error('Failed to decode base64 display wall streams props', decodeError);
            }
        }

        let props = {};
        try {
            props = JSON.parse(jsonPayload);
        } catch (error) {
            if (process.env.NODE_ENV !== 'production') {
                // eslint-disable-next-line no-console
                console.debug('DisplayWallStreams raw payload:', rawPayload);
                // eslint-disable-next-line no-console
                console.debug('DisplayWallStreams decoded payload:', jsonPayload);
            }
            console.error('Failed to parse display wall streams props', error);
        }

        const root = createRoot(node);
        node.dataset.displayWallStreamsMounted = 'true';
        root.render(<DisplayWallStreamsEditor {...props} />);
    });
}

const displayWallViewerNodes = document.querySelectorAll('[data-display-wall-viewer-props]');
if (displayWallViewerNodes.length) {
    displayWallViewerNodes.forEach((node) => {
        if (node.dataset.displayWallViewerMounted === 'true') {
            return;
        }

        let props = {};
        try {
            props = JSON.parse(node.dataset.displayWallViewerProps || '{}');
        } catch (error) {
            console.error('Failed to parse display wall viewer props', error);
        }

        const root = createRoot(node);
        node.dataset.displayWallViewerMounted = 'true';
        root.render(<DisplayWallViewer {...props} />);
    });
}

const streamManagerNodes = document.querySelectorAll('[data-stream-manager-props]');
if (streamManagerNodes.length) {
    streamManagerNodes.forEach((node) => {
        if (node.dataset.streamManagerMounted === 'true') {
            return;
        }

        const rawPayload = node.dataset.streamManagerProps || '{}';
        const isBase64 = node.dataset.streamManagerPropsB64 === '1';

        let jsonPayload = rawPayload;
        if (isBase64) {
            try {
                jsonPayload = atob(rawPayload);
            } catch (error) {
                console.error('Failed to decode stream manager props payload', error);
            }
        }

        let props = {};
        try {
            props = JSON.parse(jsonPayload);
        } catch (error) {
            if (process.env.NODE_ENV !== 'production') {
                // eslint-disable-next-line no-console
                console.debug('StreamManagement raw payload:', rawPayload);
                // eslint-disable-next-line no-console
                console.debug('StreamManagement decoded payload:', jsonPayload);
            }
            console.error('Failed to parse stream manager props', error);
        }

        const root = createRoot(node);
        node.dataset.streamManagerMounted = 'true';
        root.render(<StreamManagementApp {...props} />);
    });
}

const gateManagerNodes = document.querySelectorAll('[data-gate-manager-props]');
if (gateManagerNodes.length) {
    gateManagerNodes.forEach((node) => {
        if (node.dataset.gateManagerMounted === 'true') {
            return;
        }

        const rawPayload = node.dataset.gateManagerProps || '{}';
        const isBase64 = node.dataset.gateManagerPropsB64 === '1';
        let jsonPayload = rawPayload;
        if (isBase64) {
            try {
                jsonPayload = atob(rawPayload);
            } catch (error) {
                console.error('Failed to decode gate manager props payload', error);
            }
        }

        let props = {};
        try {
            props = JSON.parse(jsonPayload);
        } catch (error) {
            console.error('Failed to parse gate manager props', error);
        }

        const root = createRoot(node);
        node.dataset.gateManagerMounted = 'true';
        root.render(<GateManagementApp {...props} />);
    });
}

const mountGateTriggerControls = (scope = document) => {
    const nodes = scope.querySelectorAll('[data-gate-trigger-controls-props]');
    if (!nodes.length) {
        return;
    }

    nodes.forEach((node) => {
        if (node.dataset.gateTriggerControlsMounted === 'true') {
            return;
        }

        const rawPayload = node.dataset.gateTriggerControlsProps || '{}';
        const isBase64 = node.dataset.gateTriggerControlsPropsB64 === '1';
        let jsonPayload = rawPayload;
        if (isBase64) {
            try {
                jsonPayload = atob(rawPayload);
            } catch (error) {
                console.error('Failed to decode gate trigger controls props payload', error);
            }
        }

        let props = {};
        try {
            props = JSON.parse(jsonPayload);
        } catch (error) {
            console.error('Failed to parse gate trigger controls props', error);
        }

        const root = createRoot(node);
        node.dataset.gateTriggerControlsMounted = 'true';
        root.render(<GateTriggerControls {...props} />);
    });
};

window.mountGateTriggerControls = mountGateTriggerControls;
mountGateTriggerControls(document);

const gpioAccessListNodes = document.querySelectorAll('[data-gpio-access-lists-props]');
if (gpioAccessListNodes.length) {
    gpioAccessListNodes.forEach((node) => {
        if (node.dataset.gpioAccessListsMounted === 'true') {
            return;
        }

        const rawPayload = node.dataset.gpioAccessListsProps || '{}';
        const isBase64 = node.dataset.gpioAccessListsPropsB64 === '1';
        let jsonPayload = rawPayload;
        if (isBase64) {
            try {
                jsonPayload = atob(rawPayload);
            } catch (error) {
                console.error('Failed to decode GPIO access lists props payload', error);
            }
        }

        let props = {};
        try {
            props = JSON.parse(jsonPayload);
        } catch (error) {
            console.error('Failed to parse GPIO access lists props', error);
        }

        const root = createRoot(node);
        node.dataset.gpioAccessListsMounted = 'true';
        root.render(<GpioAccessListManagementApp {...props} />);
    });
}

const personPlateAssignmentsRoot = document.getElementById('person-plate-assignments-root');
if (personPlateAssignmentsRoot) {
    const rawPayload = personPlateAssignmentsRoot.dataset.personPlateAssignmentsProps || '{}';
    const isBase64 = personPlateAssignmentsRoot.dataset.personPlateAssignmentsPropsB64 === '1';
    let jsonPayload = rawPayload;
    if (isBase64) {
        try {
            jsonPayload = atob(rawPayload);
        } catch (error) {
            console.error('Failed to decode person plate assignment props payload', error);
        }
    }

    let props = {};
    try {
        props = JSON.parse(jsonPayload);
    } catch (error) {
        console.error('Failed to parse person plate assignment props', error);
    }

    const root = createRoot(personPlateAssignmentsRoot);
    root.render(<PersonPlateAssignments {...props} />);
}


document.querySelectorAll('[data-traffic-light-manager-props]').forEach((node) => {
    const props = JSON.parse(node.dataset.trafficLightManagerProps || '{}');
    createRoot(node).render(<TrafficLightManagementApp {...props} />);
});
